import React, { useEffect, useState } from 'react';

type PageModule = { default: React.ComponentType<any> };

const cache = new Map<string, React.ComponentType<any>>();
const inflight = new Map<string, Promise<React.ComponentType<any>>>();
const preloads = new Map<string, () => void>();

function loadPage(id: string, loader: () => Promise<PageModule>): Promise<React.ComponentType<any>> {
  const hit = cache.get(id);
  if (hit) return Promise.resolve(hit);
  const pending = inflight.get(id);
  if (pending) return pending;
  const next = loader()
    .then((mod) => {
      cache.set(id, mod.default);
      inflight.delete(id);
      return mod.default;
    })
    .catch((err) => {
      inflight.delete(id);
      throw err;
    });
  inflight.set(id, next);
  return next;
}

/**
 * Route component that is already in the main bundle.
 * Navigation commits immediately (menu switches) and this renders `fallback`
 * until the page chunk arrives. A cached chunk renders on the first paint.
 */
export function lazyRoute<P extends object = any>(
  paths: string | string[],
  loader: () => Promise<PageModule>,
  fallback: React.ReactNode,
): React.FC<P> {
  const ids = Array.isArray(paths) ? paths : [paths];
  const id = ids[0];
  const preload = () => {
    void loadPage(id, loader).catch(() => undefined);
  };
  ids.forEach((path) => preloads.set(path, preload));

  const Page: React.FC<P> = (props) => {
    const [Comp, setComp] = useState<React.ComponentType<any> | null>(() => cache.get(id) ?? null);
    const [failed, setFailed] = useState(false);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
      const hit = cache.get(id);
      if (hit) {
        setComp(() => hit);
        setFailed(false);
        return;
      }
      let alive = true;
      loadPage(id, loader)
        .then((component) => {
          if (!alive) return;
          setComp(() => component);
          setFailed(false);
        })
        .catch(() => {
          if (alive) setFailed(true);
        });
      return () => {
        alive = false;
      };
    }, [attempt]);

    if (failed) {
      return (
        <div style={{ padding: 24 }}>
          <p style={{ margin: '0 0 12px', color: 'var(--text-secondary, #64748b)' }}>
            This page didn’t load.
          </p>
          <button type="button" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
        </div>
      );
    }
    if (!Comp) return <>{fallback}</>;
    return <Comp {...props} />;
  };

  return Page;
}

/** Start downloading the page for a menu path (hover or click). */
export function preloadRoute(path: string): void {
  const exact = preloads.get(path);
  if (exact) {
    exact();
    return;
  }
  const pathname = path.split('?')[0];
  let best = '';
  preloads.forEach((_fn, key) => {
    if (pathname === key || pathname.startsWith(`${key}/`)) {
      if (key.length > best.length) best = key;
    }
  });
  if (best) preloads.get(best)?.();
}
